import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { enqueueCommand } from '@/offline'
import { Btn, Cta, Footer, Icon, ItemRow, Strip, V1Screen, WaitCta } from '@/features/gate/v1/kit'
import { V1Photo } from '@/features/gate/v1/photo'
import { LiveScanner, Refused, TypeCodeSheet } from '@/features/gate/v1/scan'
import { C } from '@/features/gate/v1/style'
import { shortCode, tr } from '@/features/gate/v1/text'
import type { TagEntry } from '@/features/gate/commands'
import { DoneScreen } from '@/features/store/v1/bits'
import type { BindMachineLabelPayload } from '../commands'
import '../commands'
import type { ProcessState } from '../projection'
import { checkFreshCode, rollOf } from '../model/rules'
import { k } from './icons'
import { LeadBox, NextArrow, PHeader, SaveError, saveFailed, SumCard, TickDot } from './ui'
import { refusalText, WithSite } from './parts'

/**
 * "नंबर लिखें" for a machine label: the digits printed under it, on a MACHINE roll of this site
 * (with the roll's zero padding when the site list says so). Anything else is upper-cased.
 */
function expandMachineLabel(state: ProcessState, raw: string): string {
  const c = raw.trim().toUpperCase()
  if (!/^\d+$/.test(c)) return c
  const n = Number(c)
  for (const r of state.pack.rolls) {
    if (r.kind !== 'MACHINE' || n < r.start_sequence || n > r.end_sequence) continue
    const digits = typeof r.sequence_digits === 'number' ? r.sequence_digits : c.length
    const code = `${r.payload_prefix}${String(n).padStart(digits, '0')}`.toUpperCase()
    if (rollOf([r], code)) return code
  }
  return c
}

/**
 * F6.9 — मशीन लेबल (V1): pick the machine with no label, scan the new MACHINE label stuck on it,
 * photograph it, save. Same rules and bind_machine_label command as the full MachineLabelPage.
 */
export function V1MachineLabel() {
  return <WithSite>{(state) => <MachineLabel state={state} />}</WithSite>
}

function MachineLabel({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  const actor = useActor()
  const [machineId, setMachineId] = useState<string | null>(null)
  const [stage, setStage] = useState<'machine' | 'scan' | 'photo'>('machine')
  const [code, setCode] = useState<{ code: string; entry: TagEntry } | null>(null)
  const [photo, setPhoto] = useState<string | null>(null)
  const [camera, setCamera] = useState(false)
  const [scanErr, setScanErr] = useState<string | null>(null)
  const [typing, setTyping] = useState(false)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<{ machine: string; code: string } | null>(null)
  const home = () => navigate('/m/process')
  const unlabelled = [...state.machines.values()].filter((m) => !m.label_payload)
  const machine = machineId ? state.machines.get(machineId) : undefined

  if (done)
    return (
      <DoneScreen
        title={tr('process.v1.machineLabel.doneTitle')}
        lines={tr('process.v1.machineLabel.doneLine', {
          machine: done.machine,
          code: shortCode(done.code),
        })}
        chips={[]}
        onHome={home}
      />
    )

  // F6.9-a — which machine has no label?
  if (stage === 'machine' || !machine)
    return (
      <V1Screen
        header={
          <PHeader onBack={home} icon="machine" title={tr('process.v1.machineLabel.title')} />
        }
        footer={
          <Footer>
            {machine ? (
              <Cta label={tr('process.v1.next')} icon="next" onClick={() => setStage('scan')} />
            ) : (
              <WaitCta>
                {unlabelled.length
                  ? tr('process.v1.machineLabel.pickWait')
                  : tr('process.v1.machineLabel.allLabelled')}
              </WaitCta>
            )}
          </Footer>
        }
      >
        {unlabelled.length === 0 ? (
          <Strip icon="ok" iconBox>
            {tr('process.v1.machineLabel.allLabelled')}
          </Strip>
        ) : (
          <Strip icon="info">{tr('process.v1.machineLabel.pickHint')}</Strip>
        )}
        {unlabelled.map((m) => (
          <ItemRow
            key={m.id}
            padding="8px 10px"
            onClick={() => setMachineId(m.id)}
            border={m.id === machineId ? C.ink : C.border}
            borderWidth={m.id === machineId ? 3 : 1.5}
            lead={<LeadBox icon="machine" />}
            title={`${m.code} · ${m.name}`}
            line={m.category ? <span style={{ color: C.muted }}>{m.category}</span> : undefined}
            right={m.id === machineId ? <TickDot size={26} /> : <NextArrow />}
          />
        ))}
      </V1Screen>
    )

  const machineRow = (
    <ItemRow
      padding="8px 10px"
      lead={<LeadBox icon="machine" />}
      title={`${machine.code} · ${machine.name}`}
      line={
        <span style={{ color: C.muted }}>
          {tr('process.v1.machineLabel.site', {
            site: state.pack.site_name ?? state.pack.site_code ?? '—',
          })}
        </span>
      }
    />
  )

  const check = (raw: string, entry: TagEntry): string | null => {
    const c = entry === 'TYPED' ? expandMachineLabel(state, raw) : raw.trim().toUpperCase()
    const v = checkFreshCode(state, c, 'MACHINE', actor.user_id)
    if (!v.ok) return refusalText(v.reason)
    setCode({ code: v.value, entry })
    setStage('photo')
    return null
  }

  // F6.9-b — scan the new label on the machine
  if (stage === 'scan' || !code)
    return (
      <V1Screen
        main={{ gap: 9 }}
        header={
          <PHeader
            onBack={() => setStage('machine')}
            icon="tag"
            title={tr('process.v1.machineLabel.scanTitle')}
          />
        }
        footer={
          <Footer>
            <WaitCta>{tr('process.v1.machineLabel.scanHint')}</WaitCta>
          </Footer>
        }
        overlay={
          typing ? (
            <TypeCodeSheet
              title={tr('process.v1.machineLabel.typeTitle')}
              check={(raw, entry) => {
                setScanErr(null)
                return check(raw, entry)
              }}
              onClose={() => setTyping(false)}
            />
          ) : undefined
        }
      >
        {machineRow}
        {!typing && (
          <LiveScanner
            icon="tag"
            hint={tr('process.v1.machineLabel.scanHint')}
            minHeight={160}
            check={check}
            onError={setScanErr}
          />
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn icon="keypad" onClick={() => setTyping(true)}>
            {tr('gate.v1.tag.type')}
          </Btn>
        </div>
        {scanErr && <Refused text={scanErr} />}
      </V1Screen>
    )

  const bind = async () => {
    if (!photo) return
    setSaving(true)
    setErr(null)
    try {
      const payload: BindMachineLabelPayload = {
        equipment_id: machine.id,
        qr_payload: code.code,
        entry: code.entry,
        photo_id: photo,
      }
      await enqueueCommand({
        type: 'bind_machine_label',
        payload,
        actorId: actor.user_id,
        mediaIds: [photo],
      })
      setDone({ machine: machine.code, code: code.code })
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  // F6.9-c — photo of the label on the machine, then save
  return (
    <V1Screen
      header={
        <PHeader
          onBack={() => {
            setCode(null)
            setStage('scan')
          }}
          icon="camera"
          title={tr('process.v1.machineLabel.photoTitle')}
        />
      }
      footer={
        <Footer>
          <SaveError text={err} />
          {photo ? (
            <Cta
              label={tr('process.v1.machineLabel.save')}
              icon="ok"
              busy={saving}
              onClick={() => void bind()}
            />
          ) : (
            <Cta
              label={tr('process.v1.machineLabel.takePhoto')}
              icon={k('camera')}
              lead
              onClick={() => setCamera(true)}
            />
          )}
        </Footer>
      }
    >
      <SumCard
        rows={[
          { label: tr('process.v1.machineLabel.machine'), value: <b>{machine.code}</b> },
          {
            label: tr('process.v1.machineLabel.label'),
            value: <b>{shortCode(code.code)}</b>,
          },
          {
            label: tr('process.v1.machineLabel.siteLabel'),
            value: state.pack.site_name ?? state.pack.site_code ?? '—',
          },
        ]}
      />
      <V1Photo
        value={photo}
        onChange={setPhoto}
        open={camera}
        onOpenChange={setCamera}
        render={({ take, preview, busy, failed }) => (
          <>
            <ItemRow
              onClick={take}
              padding="8px 10px"
              border={photo ? C.ok : C.border}
              borderWidth={photo ? 2 : 1.5}
              lead={
                preview ? (
                  <img
                    src={preview}
                    alt=""
                    style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 10 }}
                  />
                ) : (
                  <LeadBox icon="camera" />
                )
              }
              title={tr('process.v1.machineLabel.photo')}
              line={
                <span style={{ color: photo ? C.ok : C.muted, fontWeight: photo ? 700 : 400 }}>
                  {busy
                    ? tr('gate.v1.photo.saving')
                    : photo
                      ? tr('process.v1.machineLabel.photoTaken')
                      : tr('process.v1.machineLabel.photoHint')}
                </span>
              }
              right={
                photo ? <Icon name="camera" size={24} style={{ color: C.acc }} /> : <NextArrow />
              }
            />
            {failed && <Refused text={tr('process.v1.machineLabel.photoFailed')} />}
          </>
        )}
      />
    </V1Screen>
  )
}
